/**
 * Virtual / bot accounts — list with aggregate asset stats (ORB, SP1, SP2, …).
 */
import { useState } from 'react';
import {
  Button,
  Modal,
  Form,
  Switch,
  Input,
  Space,
  Tag,
  Card,
  Statistic,
  Row,
  Col,
  App,
  Typography,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import { RobotOutlined } from '@ant-design/icons';
import { DataTable } from '../../components/DataTable';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';

type BotAssetTotals = Record<string, string>;

interface BotStats {
  total_bots: number;
  total_users: number;
  bot_master_count: number;
  orb_wallet_total: string;
  asset_totals: BotAssetTotals;
}

interface BotFlagRow {
  id: number;
  username: string;
  phone: string | null;
  level_code: string;
  status: string;
  is_bot: boolean;
  bot_note: string | null;
  master_id: number | null;
  create_time: string;
  node_user_id?: number | null;
  assets: BotAssetTotals;
}

function fmtAsset(v: string | undefined): string {
  const n = Number(v ?? '0');
  if (!Number.isFinite(n) || n === 0) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
  return n.toPrecision(3);
}

const STAT_CARDS: Array<{ key: keyof BotAssetTotals | 'orb_wallet_total'; label: string; highlight?: boolean }> = [
  { key: 'orb_wallet_total', label: 'ORB（主+子）', highlight: true },
  { key: 'sp1', label: 'SP1' },
  { key: 'sp2', label: 'SP2' },
  { key: 'sp3', label: 'SP3' },
  { key: 'ep1', label: 'EP1' },
  { key: 'ep2', label: 'EP2' },
  { key: 'rp', label: 'RP' },
  { key: 'tp', label: 'TP' },
  { key: 'fc', label: 'FC' },
  { key: 'uo', label: 'ORB 钱包' },
];

export default function BotFlagPage() {
  const { message } = App.useApp();
  const hasNode = useAuthStore((s) => s.hasNode);
  const [botFilter, setBotFilter] = useState<string>('true');
  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState<BotFlagRow | null>(null);
  const [editForm] = Form.useForm();
  const [refreshKey, setRefreshKey] = useState(0);

  const canUpdate = hasNode('bot_flag.update');

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['bot-flag-stats', refreshKey],
    queryFn: () => api.get<BotStats>('/api/admin/bot-flags/stats'),
    staleTime: 30_000,
  });

  const openEdit = (row: BotFlagRow) => {
    setEditRow(row);
    editForm.setFieldsValue({ is_bot: row.is_bot, bot_note: row.bot_note || '' });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editRow) return;
    const nodeId = editRow.node_user_id ?? editRow.id;
    if (editRow.node_user_id == null) {
      message.warning('该账户仅存在于 PHP 镜像，无法设置 is_bot');
      return;
    }
    try {
      const values = await editForm.validateFields();
      await api.patch(`/api/admin/bot-flags/${nodeId}`, values);
      message.success('虚拟用户标记已更新');
      setEditOpen(false);
      setRefreshKey((k) => k + 1);
    } catch (e: unknown) {
      const err = e as { message?: string };
      message.error(err?.message || '更新失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 72, fixed: 'left' as const },
    { title: '用户名', dataIndex: 'username', width: 130, fixed: 'left' as const },
    {
      title: '类型',
      width: 88,
      render: (_: unknown, row: BotFlagRow) =>
        row.master_id != null ? (
          <Tag>子账户</Tag>
        ) : (
          <Tag color="orange" icon={<RobotOutlined />}>
            虚拟主号
          </Tag>
        ),
    },
    { title: '等级', dataIndex: 'level_code', width: 64 },
    {
      title: 'ORB',
      width: 96,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.uo),
    },
    {
      title: 'SP1',
      width: 96,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.sp1),
    },
    {
      title: 'SP2',
      width: 96,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.sp2),
    },
    {
      title: 'SP3',
      width: 88,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.sp3),
    },
    {
      title: 'EP1',
      width: 88,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.ep1),
    },
    {
      title: 'RP',
      width: 88,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.rp),
    },
    {
      title: 'TP',
      width: 88,
      render: (_: unknown, row: BotFlagRow) => fmtAsset(row.assets.tp),
    },
    { title: '备注', dataIndex: 'bot_note', width: 160, ellipsis: true, render: (v: string | null) => v || '—' },
    {
      title: '状态',
      dataIndex: 'status',
      width: 72,
      render: (v: string) => <Tag color={v === 'active' ? 'green' : 'red'}>{v}</Tag>,
    },
    ...(canUpdate
      ? [
          {
            title: '操作',
            width: 72,
            fixed: 'right' as const,
            render: (_: unknown, row: BotFlagRow) =>
              row.node_user_id != null ? (
                <Button size="small" onClick={() => openEdit(row)}>
                  设置
                </Button>
              ) : (
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  镜像
                </Typography.Text>
              ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>虚拟账户</h2>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        与旧版「虚拟用户」一致：PHP 镜像中纯数字账号（account_kind=bot）。余额来自镜像表；ORB 合计使用
        floor(raw_uo + 挂单×7/8) 算法。
      </Typography.Paragraph>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" loading={statsLoading}>
            <Statistic
              title="虚拟账户数"
              value={stats?.total_bots ?? 0}
              prefix={<RobotOutlined />}
              valueStyle={{ color: '#fa8c16', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6} lg={4}>
          <Card size="small" loading={statsLoading}>
            <Statistic title="虚拟主号" value={stats?.bot_master_count ?? 0} valueStyle={{ fontSize: 20 }} />
          </Card>
        </Col>
        {STAT_CARDS.map(({ key, label, highlight }) => {
          const raw =
            key === 'orb_wallet_total'
              ? stats?.orb_wallet_total
              : stats?.asset_totals?.[key as string];
          const n = Number(raw ?? '0');
          return (
            <Col xs={12} sm={8} md={6} lg={4} key={key}>
              <Card size="small" loading={statsLoading}>
                <Statistic
                  title={label}
                  value={Number.isFinite(n) ? n : 0}
                  precision={key === 'orb_wallet_total' ? 0 : 2}
                  valueStyle={{
                    fontSize: highlight ? 18 : 16,
                    color: highlight ? '#cf1322' : undefined,
                  }}
                />
              </Card>
            </Col>
          );
        })}
      </Row>

      <Space style={{ marginBottom: 12 }}>
        <select
          value={botFilter}
          onChange={(e) => setBotFilter(e.target.value)}
          style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #d9d9d9' }}
        >
          <option value="true">仅虚拟账户</option>
          <option value="false">仅真实用户</option>
          <option value="">全部用户</option>
        </select>
      </Space>

      <DataTable<BotFlagRow>
        queryKey={['admin-bot-flags', botFilter, refreshKey]}
        endpoint="/api/admin/bot-flags"
        extraParams={botFilter === '' ? {} : { is_bot: botFilter }}
        columns={columns}
        rowKey="id"
        scroll={{ x: 1200 }}
      />

      <Modal
        title={`设置虚拟用户 — ${editRow?.username ?? ''}`}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        onOk={handleEdit}
        okText="确认"
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="is_bot" label="标记为虚拟用户" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="bot_note" label="备注">
            <Input.TextArea rows={2} maxLength={255} placeholder="说明标记原因（可选）" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
